import { NumberOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { Form, Input, QRCode, Spin, Typography } from 'antd';
import Modal from 'components/Modal';
import PhoneNumber from 'components/PhoneNumber';
import { useMutation } from 'hooks/apollo';
import React, { useEffect, useState } from 'react';
import { Mfa } from 'shared/prisma/types';
import icon from 'shared/favicon/android-chrome-192x192.png';

const ENABLE_MFA = gql`
  mutation($userId: ID!, $mfaCode: String, $mfaMethod: MFA) {
    enableMfa(userId: $userId, mfaCode: $mfaCode, mfaMethod: $mfaMethod) {
      id
      mfa
      defaultMfa
      totpEnabled
      mfaMethods
    }
  }
`;

const SETUP_TOTP = gql`
  mutation($userId: ID!) {
    setupTotp(userId: $userId) {
      secret
      otpauthUrl
    }
  }
`;

const MFA_METHOD_LABELS = {
  [Mfa.Sms]: 'SMS',
  [Mfa.Email]: 'Email',
  [Mfa.Totp]: 'Authenticator app',
};

const EnableMFA = ({ user, mfaMethod, reconfigure, onCancel }) => {
  const [enableMfa, { loading }] = useMutation(ENABLE_MFA);
  const [setupTotp, { loading: setupLoading }] = useMutation(SETUP_TOTP);
  const [codeSent, setCodeSent] = useState(false);
  const [totpSetup, setTotpSetup] = useState();
  const [form] = Form.useForm();

  useEffect(() => {
    setCodeSent(false);
    setTotpSetup(undefined);
    form.resetFields();
  }, [user, mfaMethod, form]);

  useEffect(() => {
    if (!user?.id || mfaMethod !== Mfa.Totp) {
      return;
    }

    setupTotp({ variables: { userId: user.id } }).then(({ data }) => {
      setTotpSetup(data.setupTotp);
    });
  }, [user?.id, mfaMethod, setupTotp]);

  const showVerificationForm = (mfaMethod === Mfa.Totp && totpSetup) || (mfaMethod !== Mfa.Totp && codeSent);
  const okText = showVerificationForm ? 'Verify' : 'Continue';

  const startSetup = async () => {
    await enableMfa({ variables: { userId: user.id, mfaMethod } });
    setCodeSent(true);
  };

  const handleOk = async () => {
    if (showVerificationForm) {
      form.submit();
    } else {
      await startSetup();
    }
  };

  const handleSubmit = async (values) => {
    try {
      await enableMfa({
        variables: {
          userId: user.id,
          mfaCode: values.mfaCode,
          mfaMethod,
        },
      });
      await onCancel();
    } catch (e) {
      form.setFields([{ name: 'mfaCode', errors: ['Invalid verification code'] }]);
    }
  };

  const methodDescription = {
    [Mfa.Sms]: (
      <>
        To enable SMS verification we need to verify your phone number. We will send a code to
        <PhoneNumber number={user?.phone} />
      </>
    ),
    [Mfa.Email]: (
      <>
        To enable email verification we will send a code to
        {' '}
        {user?.email}
      </>
    ),
    [Mfa.Totp]: reconfigure
      ? 'Scan the new QR code with your authenticator app to replace your existing setup.'
      : 'Set up an authenticator app on your phone or computer.',
  };

  const modalTitle = reconfigure
    ? `Reconfigure ${MFA_METHOD_LABELS[mfaMethod]}`
    : `Enable ${MFA_METHOD_LABELS[mfaMethod] || 'multi-factor authentication'}`;

  return (
    <Modal
      title={modalTitle}
      open={!!user && !!mfaMethod}
      onCancel={onCancel}
      okText={okText}
      onOk={handleOk}
      confirmLoading={loading || (mfaMethod === Mfa.Totp && setupLoading && !totpSetup)}
      okButtonProps={{ disabled: mfaMethod === Mfa.Totp && setupLoading && !totpSetup }}
    >
      <p>Multi-factor authentication adds an extra layer of security to your account.</p>

      {mfaMethod !== Mfa.Totp && <p>{methodDescription[mfaMethod]}</p>}

      {mfaMethod === Mfa.Totp && setupLoading && !totpSetup && (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <Spin />
        </div>
      )}

      {totpSetup && (
        <>
          <p>
            {reconfigure
              ? 'Scan the new QR code with your authenticator app, or enter the new secret manually. Then enter the 6-digit code to verify.'
              : 'Scan the QR code with your authenticator app, or enter the secret manually. Then enter the 6-digit code to verify.'}
          </p>
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 16 }}>
            <QRCode value={totpSetup.otpauthUrl} size={180} icon={icon} iconSize={40} />
            <div>
              <Typography.Text type="secondary">Secret</Typography.Text>
              <br />
              <Typography.Text copyable code>{totpSetup.secret}</Typography.Text>
            </div>
          </div>
        </>
      )}

      {showVerificationForm && (
        <Form form={form} layout="vertical" onFinish={handleSubmit} validateTrigger="onSubmit">
          <Form.Item name="mfaCode" label="Verification code" rules={[{ required: true, len: 6, message: '6 digit code is required' }]}>
            <Input
              prefix={<NumberOutlined className="input-icon" />}
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default EnableMFA;
